import type { RichTextRkpiSpeed, RichTextStyle } from "@library-elements/text";

export const RKPI_SPEED_OPTIONS: {
  [key in RichTextRkpiSpeed]: { duration: number; label: string };
} = {
  slow: {
    label: "Slow",
    duration: 6000,
  },
  medium: {
    label: "Medium",
    duration: 3000,
  },
  fast: {
    label: "Fast",
    duration: 1500,
  },
};

export const RKPI_SPEEDS = Object.keys(RKPI_SPEED_OPTIONS);

export const TEXT_STYLES: Record<
  RichTextStyle,
  {
    size: string;
    font: string;
    weight: string;
    color: string;
    /**
     * `controller.hidden` removes the entry from the toolbar dropdown but
     * keeps it in the map so legacy data can still resolve through it.
     */
    controller: {
      label: string;
      fontSize: number;
      lineHeight: string;
      hidden?: boolean;
    };
  }
> = {
  display: {
    size: "display-size",
    color: "display-color",
    font: "display-font",
    weight: "display-style",
    controller: {
      label: "Display",
      fontSize: 28,
      lineHeight: "32px",
    },
  },
  "slide-title": {
    size: "title-size",
    color: "title-color",
    font: "title-font",
    weight: "title-style",
    controller: {
      label: "Title",
      fontSize: 22,
      lineHeight: "27px",
    },
  },
  heading: {
    size: "heading-size",
    color: "heading-color",
    font: "heading-font",
    weight: "heading-style",
    controller: {
      label: "Heading",
      fontSize: 18,
      lineHeight: "22px",
    },
  },
  subheading: {
    size: "subtitle-size",
    color: "subtitle-color",
    font: "subtitle-font",
    weight: "subtitle-style",
    controller: {
      label: "Subheading",
      fontSize: 16,
      lineHeight: "20px",
    },
  },
  normal: {
    size: "paragraph-size",
    color: "paragraph-color",
    font: "paragraph-font",
    weight: "paragraph-style",
    controller: {
      label: "Normal text",
      fontSize: 13,
      lineHeight: "16px",
    },
  },
  // Legacy alias of "subheading" — kept so older entities (where the type
  // was "subtitle" mapped to h3) still resolve via TEXT_STYLES lookup. Hidden
  // from the toolbar dropdown.
  subtitle: {
    size: "subtitle-size",
    color: "subtitle-color",
    font: "subtitle-font",
    weight: "subtitle-style",
    controller: {
      label: "Subheading",
      fontSize: 16,
      lineHeight: "20px",
      hidden: true,
    },
  },
};

/**
 * Block tag the editor produces for each text style. Centralized so the
 * toolbar handler, the table cell applier, and any future code paths share
 * a single mapping. Keep in sync with `walkParentChain` in refresh.ts.
 */
export const TEXT_STYLE_TAGS: Record<RichTextStyle, string> = {
  display: "h1",
  "slide-title": "h2",
  heading: "h3",
  subheading: "h4",
  normal: "p",
  subtitle: "h4", // legacy "subtitle" type now renders as h4 to match subheading
};

// --- Magic number constants ---

/** Delay (ms) before re-activating highlight after attribute change */
export const HIGHLIGHT_RESET_DELAY_MS = 400;

/** Animation speed delays (ms) mapped by speed name */
export const ANIMATION_DELAYS: Record<string, number> = {
  slow: 1500,
  medium: 750,
  fast: 375,
};

/** Short delay (ms) to let Froala settle after application-wrapper paste */
export const PASTE_SETTLE_DELAY_MS = 10;

/** Fail-safe limit for parent-walking loops to prevent infinite iteration */
export const MAX_PARENT_WALK_DEPTH = 100;

/** Selector for collapsible text element */
export const COLLAPSIBLE_TEXT_SELECTOR = ".collapsible-text-aTdNqqZXed";

/** Selector for Liveblocks collaboration comments rich text container */
export const LIVEBLOCKS_COMMENTS_SELECTOR = "#vyqdiRYC2qsIgAX";

export const MAPPED_PLACEHOLDERS = {
  "<h2>": "<h2></h2>",
  "<h3>": "<h3></h3>",
  "<p>": "<p></p>",
  "<ul>": "<ul><li></li></ul>",
  "<ol>": "<ol><li></li></ol>",
};
